/* 
  From contentful you get a Rich Text Document, and you need instructions on how to render it.
  This file contains the instructions on how to render the Rich Text Document.

  Article and Case Study and any other place where you need to render rich text, should import this file.
*/

// export const dynamicParams = true // this currently doesn't work on netlify
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Image from 'next/image'

// Client Components
import ImageCarousel from "@/components/client/ImageCarousel";
import MarketoForm from "@/components/client/MarketoForm";
import CognitoForm from "@/components/client/CognitoForm";

// Server Components
import YoutubeVideo from "@/components/server/YoutubeVideo";
import VidyardVideo from "@/components/server/VidyardVideo";
import VimeoVideo from "@/components/server/VimeoVideo";
import WistiaVideo from "@/components/server/WistiaVideo";
import Anchor from "@/components/server/Anchor";
import LibsynEmbed from "@/components/server/LibsynEmbed";
// import caseStudyImage from "@/components/server/caseStudyImage";
import ImageCompare from "@/components/server/ImageCompare";

import FormatSectionTestimonials from '@/components/client/SectionTestimonials/FormatSectionTestimonials';
import FormatActionBlock from "@/components/client/ActionBlock/FormatActionBlock";
import ArticleImage from '@/components/server/ArticleImage';
import FeaturedText from '@/components/server/FeaturedText';
import SectionStatBlocks from '@/components/server/SectionStatBlocks/SectionStatBlocks';
import ImageStrip from '@/components/server/ImageStrip/ImageStrip';
import { EmbeddedEntryWrapper } from '@/components/client/EmbeddedEntryWrapper/EmbeddedEntryWrapper';
import ArticleCta from "@/components/server/ArticleCta/ArticleCta";
import Link from 'next/link';
import { getProfile } from '@/profiles/getProfile'
import FormatSectionFaqs from '@/components/client/SectionFaqs/FormatSectionFaqs';


const profile = getProfile(process.env.NEXT_PUBLIC_PROFILE);

interface Options {
  preserveWhitespace: boolean;
  renderMark: any;
  renderNode: any;
}

export const generalOptions: Options = {
  preserveWhitespace : true,
  renderMark: {
    // REVIEW - this creates a hydration error in the console
    // [MARKS.CODE]: (text:string) => {
    //   return <pre><code>{text}</code></pre>;
    // },
    [MARKS.BOLD]: (text:string) => {
      return <strong>{text}</strong>;
    },
    [MARKS.ITALIC]: (text:string) => {
      return <em>{text}</em>;
    },
  },
  renderNode: {
    // [BLOCKS.EMBEDDED_ASSET]: (node:any) => {
		//   // return <img src={node.data.target.url} alt="" />
    //   // this was node.data.target.fields?.file?.url
		//   return <Image src={node.data.target.url} width={800} height={800} alt="" />
    // },
    [INLINES.HYPERLINK]: (node:any, children: React.ReactNode) => {
      const { uri, title } = node.data;

      const isExternalLink: boolean = uri.includes(profile.NAV_BASE_URL) === false && uri.startsWith('http');
      return (<Link 
        href={uri} 
        className="article-link"
        target={isExternalLink ? "_blank" : "_self"}
        rel={isExternalLink ? "noopener noreferrer" : ""}
      >
        {children}
      </Link>);
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node:any) => {

      const youtubeRegex = /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)(\S+)?$/im
      const youtubeCodeRegex =/^[a-z,0-9,_,-]{11}$/gmi
      const wistiaCodeRegex =/^[a-z,0-9,_,-]{10}$/gmi
      const vimeoCodeRegex = /[1-9]{8}/gmi
      const youtubePlaylistCodeRegex =/[a-z,0-9,_,-]{34}$/gmi
      const vidyardCodeRegex =/[a-z,0-9,_,-]{22}$/gmi
      
      const type = node.data.target.__typename
      if(type === "ArticleImage") {
        return (
          <EmbeddedEntryWrapper
            childComponent={
              <ArticleImage url={node.data.target.mediaImage.url} altText={node.data.target.altText} caption={documentToReactComponents(node.data.target?.imageCaption?.json)} />
            }
            extraClasses={`relative`}
          />
        )
      } else if(type === "Image") {
        return (
          <EmbeddedEntryWrapper 
            childComponent={
              <ArticleImage url={node.data.target?.image?.url} altText={node.data.target.altText} />
            }
            extraClasses={`relative`}
          />
        )
      } else if(type === "Video") {
        const videoCode = node.data.target.videoCode
        const youtubeRegexMatches = videoCode.match(youtubeRegex)
        const youtubeRegexMatchId = youtubeRegexMatches?.[6]
        if(youtubeRegexMatchId) { // if youtube
          return (
            <EmbeddedEntryWrapper 
              childComponent={<YoutubeVideo videoID={youtubeRegexMatchId} />} 
              extraClasses={`flex justify-center`} 
            />
          )
        }
        if(youtubeCodeRegex.test(videoCode)) { // if youtube
          return (
            <EmbeddedEntryWrapper 
              childComponent={<YoutubeVideo videoID={videoCode} />} 
              extraClasses={`flex justify-center`} 
            />
          )
        }
        if(youtubePlaylistCodeRegex.test(videoCode)) { // if youtube playlist
          const playlistId = `?listType=playlist&list=${videoCode}`
          return (
            <EmbeddedEntryWrapper 
              childComponent={<YoutubeVideo videoID={playlistId} />} 
              extraClasses={`flex justify-center`} 
            />
          )
        }
        if(vidyardCodeRegex.test(videoCode)) { // if vidyard
          return (
            <EmbeddedEntryWrapper 
              childComponent={<VidyardVideo videoID={videoCode} />} 
              extraClasses={`flex justify-center`} 
            />
          )
        }
        if(vimeoCodeRegex.test(videoCode)) { // if vimeo
          return (
            <EmbeddedEntryWrapper 
              childComponent={ <VimeoVideo videoID={videoCode} /> } 
              extraClasses={`flex justify-center`} 
            />
          )
        }
        if(wistiaCodeRegex.test(videoCode)) { // if wistia
          return (
            <EmbeddedEntryWrapper 
              childComponent={ <WistiaVideo videoID={videoCode} /> } 
              extraClasses={`flex justify-center`} 
            />
          )
        }
        return <div className="content-error">{`ERROR: Unknown Video Type: ${videoCode}`}</div>
      } else if(type === "ArticleCta") {
        return (
          <EmbeddedEntryWrapper 
            childComponent={ <ArticleCta data={node.data.target}/> }
            extraClasses={`relative`}
          />
        )
      } else if(type === "ImageCompare") {
        return <ImageCompare url1={node.data.target.image1.url} url2={node.data.target.image2.url} />
        // return (
        //   <EmbeddedEntryWrapper 
        //     childComponent={ <ImageCompare url1={node.data.target.image1.url} url2={node.data.target.image2.url} /> }
        //     extraClasses={`relative`}
        //   />
        // )
      } else if(type === "Carousel") {
        if(node.data.target.carouselItemsCollection.items.length == 1) {
          const url = node.data.target.carouselItemsCollection.items[0].url
          return (
            <EmbeddedEntryWrapper 
              childComponent={ <Image src={url} width={0} height={0} alt=" " sizes="100vw" style={{ width: 'inherit', height: 'inherit' }}/> }
              extraClasses={`relative`}
            />
          )
        }
        if(node.data.target.displayAs === "Strip") {
          return (
            <EmbeddedEntryWrapper 
              childComponent={ <ImageStrip items={node.data.target.carouselItemsCollection.items} /> }
              extraClasses={`relative`}
            />
          )
        }
        return (
          <EmbeddedEntryWrapper 
            childComponent={ <ImageCarousel items={node.data.target.carouselItemsCollection.items} /> }
            extraClasses={`relative`}
          />
        )
      } else if (type === 'Image') {
        return (
          <EmbeddedEntryWrapper 
            childComponent={ <Image src={node.data?.target?.image?.url} alt={node.data?.target?.altText} width={1280} height={800} sizes="100vw" className={'caseStudy-content-image'} /> }
            extraClasses={`relative`}
          />
        )
      } else if (type === 'ActionBlock') {
        return (
          <EmbeddedEntryWrapper 
            childComponent={ <FormatActionBlock actionBlockData={node?.data?.target} /> }
            extraClasses={`narrow-container embedded-action-block`}
          />
        )
      } else if(type === "ArticleAnchor") {
        const anchorId = node.data.target.anchorId
        return <Anchor anchorId={anchorId} />
      } else if(type === "LibsynEmbed") {
        const code = node.data.target.libsynCode
        return <LibsynEmbed embedCode={code} />
      } else if (type === "FeaturedText"){
        return (
          <EmbeddedEntryWrapper 
            childComponent={ <FeaturedText data={node.data.target} /> } 
          />
        )
      } else if (type === "SectionStatBlocks"){
        return (
          <EmbeddedEntryWrapper 
            childComponent={ <SectionStatBlocks data={node.data.target} /> } 
          />
        )
      } else if(type === "CognitoForm") {
        return (
          <EmbeddedEntryWrapper 
            childComponent={ <CognitoForm cognitoScriptID={node.data.target.scriptId} cognitoFormID={node.data.target.formId} /> } 
          />
        )
      } else if(type === "MarketoForm") {
        return (
          <EmbeddedEntryWrapper 
            childComponent={ <MarketoForm formId={node.data.target.formId} baseURL={node.data.target.scriptLocation} munchkinId={node.data.target.marketoInstance} /> } 
          />
        )
      } else if(type === "SectionTestimonials") {
        return (
          <EmbeddedEntryWrapper 
            childComponent={ <FormatSectionTestimonials data={node.data?.target} /> } 
          />
        )
       } else if(type === "SectionFaqs") {
        return (
          <EmbeddedEntryWrapper 
            childComponent={ <FormatSectionFaqs data={node.data?.target} /> } 
          />
        )
       } else if(type === null || type === undefined) {
        // if elements are broken or archived, this will be null or undefined
        if(process.env.NEXT_PUBLIC_PROFILE == 'preview') {
          return <div>Element removed or added incorrectly</div>
        } else {
          return null // don't show outside of preview
        }
      } else {
        return `ERROR: Unknown Entry Type: ${type}`
      }
    },
  }
}